import { api } from '@api'
import { navigationRef } from '../navigationRef'
import { pushTarget, type PushData } from './pushTarget'

type Navigate = (name: string, params?: object) => void

const waitForNavigation = async (): Promise<boolean> => {
  // On a cold start the push arrives before the navigator is mounted.
  for (let i = 0; i < 20 && !navigationRef.isReady(); i++) {
    await new Promise((resolve) => setTimeout(resolve, 150))
  }
  return navigationRef.isReady()
}

/** Opens the screen of a tapped push: the conversation, the profile, the rendez-vous… */
export const openPushTarget = async (data: PushData | undefined | null): Promise<void> => {
  if (!(await waitForNavigation())) return
  const go = navigationRef.navigate as unknown as Navigate
  const target = pushTarget(data)

  switch (target.kind) {
    case 'conversation':
      go('Messages', {
        screen: target.isGroup ? 'GroupConversation' : 'Conversation',
        params: { conversationId: target.conversationId },
      })
      return
    case 'writeTo':
      try {
        const { data: conversation } = await api.post<{ id: string }>('/conversations/individual', {
          participantAccountId: target.contactId,
        })
        if (conversation?.id) {
          go('Messages', { screen: 'Conversation', params: { conversationId: conversation.id } })
          return
        }
      } catch {
        // The profile below still lets the user write.
      }
      go('Profile', { screen: 'DetailsGhostProfile', params: { profileId: target.contactId } })
      return
    case 'profile':
      go('Profile', { screen: 'DetailsGhostProfile', params: { profileId: target.profileId } })
      return
    case 'event':
      go('Menu', { screen: 'Calendar', params: { screen: 'AddEvent', params: { eventId: target.eventId } } })
      return
    case 'birthdays':
      go('Menu', { screen: 'Birthdays' })
      return
    default:
      go('Tab', { screen: 'Notifications' })
  }
}

/** True while this very conversation is on screen: its new messages need no banner. */
export const isViewingConversation = (conversationId: string | undefined): boolean => {
  if (!conversationId || !navigationRef.isReady()) return false
  const route = navigationRef.getCurrentRoute()
  return (
    (route?.name === 'Conversation' || route?.name === 'GroupConversation') &&
    (route.params as { conversationId?: string } | undefined)?.conversationId === conversationId
  )
}
